/**
 * Mt Forge - License Activation Page
 *
 * Standalone page where users enter their license key to activate an app.
 * Used in all Forge apps - clean, centered card design with auto-formatting input.
 *
 * Usage in any app's router:
 *   import LicenseActivationPage from './pages/LicenseActivationPage';
 *   <Route path="/activate" element={<LicenseActivationPage appSlug="croppatrol" appName="CropPatrol" />} />
 *
 * Props:
 *   appSlug  - The app identifier (e.g. "croppatrol")
 *   appName  - Display name (e.g. "CropPatrol")
 *   userEmail - Current user's email (from AuthContext)
 *   onSuccess - Callback after successful activation (optional, e.g. navigate to dashboard)
 */

import React, { useState } from 'react';
import { activateLicense, isValidKeyFormat } from '../licenseUtils';

export default function LicenseActivationPage({ appSlug, appName, userEmail, onSuccess }) {
  const [keyInput, setKeyInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [isError, setIsError] = useState(false);
  const [activated, setActivated] = useState(false);

  // ============================================================
  // Auto-format key input: XX-XXXX-XXXX-XXXX
  // ============================================================
  const handleKeyChange = (e) => {
    let val = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');

    // Remove existing dashes for re-formatting
    const raw = val.replace(/-/g, '');

    if (raw.length <= 2) {
      val = raw;
    } else if (raw.length <= 6) {
      val = raw.slice(0, 2) + '-' + raw.slice(2);
    } else if (raw.length <= 10) {
      val = raw.slice(0, 2) + '-' + raw.slice(2, 6) + '-' + raw.slice(6);
    } else {
      val = raw.slice(0, 2) + '-' + raw.slice(2, 6) + '-' + raw.slice(6, 10) + '-' + raw.slice(10, 14);
    }

    setKeyInput(val);
    setMessage(null);
    setIsError(false);
  };

  // ============================================================
  // Handle activation
  // ============================================================
  const handleActivate = async () => {
    const cleanKey = keyInput.trim().toUpperCase();

    if (!cleanKey) {
      setMessage('Please enter a license key.');
      setIsError(true);
      return;
    }

    if (!isValidKeyFormat(cleanKey)) {
      setMessage('Invalid key format. Expected: XX-XXXX-XXXX-XXXX');
      setIsError(true);
      return;
    }

    if (!userEmail) {
      setMessage('You must be logged in to activate a license key.');
      setIsError(true);
      return;
    }

    setLoading(true);
    setMessage(null);

    const result = await activateLicense(cleanKey, userEmail, appSlug);

    setLoading(false);

    if (result.success) {
      setMessage(result.message);
      setIsError(false);
      setActivated(true);

      // Auto-redirect after short delay
      if (onSuccess) {
        setTimeout(() => onSuccess(result), 2000);
      }
    } else {
      setMessage(result.message);
      setIsError(true);
    }
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Logo / Brand */}
        <div style={styles.brandArea}>
          <div style={styles.logoCircle}>
            <span style={styles.logoText}>{(appName || appSlug || 'MF').slice(0, 2).toUpperCase()}</span>
          </div>
          <h1 style={styles.appName}>{appName || appSlug}</h1>
          <p style={styles.brandSub}>Powered by Mt Forge</p>
        </div>

        {/* Success State */}
        {activated ? (
          <div style={styles.successContainer}>
            <div style={styles.successIcon}>
              <span style={{ fontSize: 32 }}>OK</span>
            </div>
            <h2 style={styles.successTitle}>License Activated!</h2>
            <p style={styles.successText}>
              Your license for <strong>{appName || appSlug}</strong> has been successfully activated.
              {onSuccess ? ' Redirecting to your dashboard...' : ''}
            </p>
            {!onSuccess && (
              <button
                onClick={() => window.location.href = '/dashboard'}
                style={styles.primaryBtn}
              >
                Go to Dashboard
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Instructions */}
            <div style={styles.instructions}>
              <h2 style={styles.instructionTitle}>Activate Your License</h2>
              <p style={styles.instructionText}>
                Enter your registration key below to activate <strong>{appName || appSlug}</strong>.
                Your key was provided in your purchase confirmation email.
              </p>
            </div>

            {/* Key Input */}
            <div style={styles.inputWrapper}>
              <label style={styles.inputLabel}>License Key</label>
              <input
                type="text"
                value={keyInput}
                onChange={handleKeyChange}
                placeholder="XX-XXXX-XXXX-XXXX"
                maxLength={17}
                style={styles.keyInput}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && !loading && handleActivate()}
                disabled={loading}
              />
              <div style={styles.inputHint}>
                Format: 2 letter prefix, followed by 3 groups of 4 characters
              </div>
            </div>

            {/* Activate Button */}
            <button
              onClick={handleActivate}
              disabled={loading || !keyInput}
              style={{
                ...styles.primaryBtn,
                opacity: (loading || !keyInput) ? 0.6 : 1,
                cursor: (loading || !keyInput) ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <span style={styles.btnSpinner} />
                  Activating...
                </span>
              ) : (
                'Activate License'
              )}
            </button>

            {/* Message */}
            {message && (
              <div style={{
                ...styles.messageBox,
                background: isError ? '#451a1a' : '#0f2a1f',
                borderColor: isError ? '#ef4444' : '#10b981',
                color: isError ? '#fca5a5' : '#6ee7b7',
              }}>
                {message}
              </div>
            )}
          </>
        )}

        {/* Divider */}
        <div style={styles.divider} />

        {/* Contact Sales Footer */}
        <div style={styles.footer}>
          <p style={styles.footerQuestion}>
            Don't have a license key?
          </p>
          <a href="mailto:r.miskulin@mtforge.ai" style={styles.contactBtn}>
            Contact Sales
          </a>
          <div style={styles.footerLinks}>
            <a href="https://miskulintechnologies.com" style={styles.footerLink} target="_blank" rel="noopener noreferrer">
              miskulintechnologies.com
            </a>
            <span style={styles.footerDot}>|</span>
            <a href="mailto:r.miskulin@mtforge.ai" style={styles.footerLink}>
              r.miskulin@mtforge.ai
            </a>
          </div>
          <p style={styles.footerAddress}>
            121 Rainbow Falls Bay, Alberta
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// INLINE STYLES
// ============================================================
const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#0f172a',
    padding: 24,
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  card: {
    width: '100%',
    maxWidth: 480,
    background: '#1e293b',
    borderRadius: 20,
    border: '1px solid #334155',
    padding: '48px 40px 36px',
    textAlign: 'center',
    boxShadow: '0 25px 50px rgba(0,0,0,0.4)',
  },
  // Brand area
  brandArea: {
    marginBottom: 32,
  },
  logoCircle: {
    width: 72,
    height: 72,
    background: 'linear-gradient(135deg, #4F46E5, #7c3aed)',
    borderRadius: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
    boxShadow: '0 8px 24px rgba(79,70,229,0.3)',
  },
  logoText: {
    fontSize: 26,
    fontWeight: 800,
    color: '#fff',
    letterSpacing: -1,
  },
  appName: {
    fontSize: 22,
    fontWeight: 800,
    color: '#f1f5f9',
    margin: '0 0 4px',
  },
  brandSub: {
    fontSize: 12,
    color: '#64748b',
    margin: 0,
    letterSpacing: 0.5,
  },
  // Instructions
  instructions: {
    marginBottom: 28,
  },
  instructionTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#e2e8f0',
    margin: '0 0 8px',
  },
  instructionText: {
    fontSize: 13,
    color: '#94a3b8',
    lineHeight: 1.6,
    margin: 0,
  },
  // Input
  inputWrapper: {
    marginBottom: 20,
    textAlign: 'left',
  },
  inputLabel: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#94a3b8',
    marginBottom: 8,
  },
  keyInput: {
    width: '100%',
    padding: '16px 18px',
    background: '#0f172a',
    border: '2px solid #334155',
    borderRadius: 12,
    color: '#e2e8f0',
    fontSize: 20,
    fontFamily: '"JetBrains Mono", "SF Mono", "Fira Code", "Cascadia Code", monospace',
    letterSpacing: 2,
    textAlign: 'center',
    textTransform: 'uppercase',
    outline: 'none',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  },
  inputHint: {
    fontSize: 11,
    color: '#475569',
    marginTop: 6,
    textAlign: 'center',
  },
  // Primary button
  primaryBtn: {
    width: '100%',
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #4F46E5, #7c3aed)',
    border: 'none',
    borderRadius: 12,
    color: '#fff',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.15s',
    marginBottom: 16,
  },
  btnSpinner: {
    display: 'inline-block',
    width: 16,
    height: 16,
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  // Message box
  messageBox: {
    padding: '12px 16px',
    borderRadius: 10,
    border: '1px solid',
    fontSize: 13,
    fontWeight: 500,
    textAlign: 'center',
    marginBottom: 8,
  },
  // Success state
  successContainer: {
    padding: '16px 0',
  },
  successIcon: {
    width: 72,
    height: 72,
    background: 'linear-gradient(135deg, #10b981, #059669)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    color: '#fff',
    fontWeight: 800,
  },
  successTitle: {
    fontSize: 22,
    fontWeight: 800,
    color: '#10b981',
    margin: '0 0 8px',
  },
  successText: {
    fontSize: 14,
    color: '#94a3b8',
    lineHeight: 1.6,
    margin: '0 0 24px',
  },
  // Footer
  divider: {
    height: 1,
    background: '#334155',
    margin: '24px 0',
  },
  footer: {
    textAlign: 'center',
  },
  footerQuestion: {
    fontSize: 14,
    color: '#94a3b8',
    margin: '0 0 12px',
    fontWeight: 500,
  },
  contactBtn: {
    display: 'inline-block',
    padding: '10px 28px',
    background: 'transparent',
    border: '2px solid #4F46E5',
    borderRadius: 10,
    color: '#4F46E5',
    fontSize: 14,
    fontWeight: 700,
    textDecoration: 'none',
    transition: 'all 0.15s',
    marginBottom: 16,
  },
  footerLinks: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    flexWrap: 'wrap',
    marginBottom: 6,
  },
  footerLink: {
    color: '#475569',
    fontSize: 11,
    textDecoration: 'none',
  },
  footerDot: {
    color: '#334155',
    fontSize: 11,
  },
  footerAddress: {
    color: '#334155',
    fontSize: 10,
    margin: 0,
    letterSpacing: 0.3,
  },
};
